import { useState } from "react";

import type { TallyDutyLedger, TallyTaxMapRow, TallyTaxSide } from "../../data/tallyTaxApi";
import { CoButton } from "../../components/Button";

const INPUT_CLASS = "h-9 rounded-md border bg-background px-2 text-sm";

export function TallyTaxMapTable({
  rows,
  dutyLedgers,
  busy,
  createLocked = false,
  onMap,
  onCreate,
}: {
  rows: TallyTaxMapRow[];
  dutyLedgers: TallyDutyLedger[];
  busy: boolean;
  /** Another ledger is still being created. Other Create buttons stay off until it finishes. */
  createLocked?: boolean;
  onMap: (row: TallyTaxMapRow, ledgerName: string, side?: "invoice" | "bill") => void;
  onCreate: (row: TallyTaxMapRow, ledgerName: string, side?: "invoice" | "bill") => void;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No rates in the catalogue yet.</p>;
  }

  const paired = rows.some((row) => row.bill && row.invoice);

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className="w-64 px-3 py-2 font-medium">Name</th>
            {paired ? (
              <>
                <th className="px-3 py-2 font-medium">Input ledger (bills)</th>
                <th className="px-3 py-2 font-medium">Output ledger (invoices)</th>
              </>
            ) : (
              <th className="px-3 py-2 font-medium">Ledger under Duties &amp; Taxes</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) =>
            paired && row.bill && row.invoice ? (
              <tr key={`${row.kind}-${row.id}`} className="border-t align-top">
                <td className="px-3 py-2.5">
                  <RateName row={row} />
                </td>
                <td className="px-3 py-2.5">
                  <SideCell
                    row={row}
                    side="bill"
                    ledger={row.bill}
                    dutyLedgers={dutyLedgers}
                    busy={busy}
                    locked={createLocked}
                    onMap={onMap}
                    onCreate={onCreate}
                  />
                </td>
                <td className="px-3 py-2.5">
                  <SideCell
                    row={row}
                    side="invoice"
                    ledger={row.invoice}
                    dutyLedgers={dutyLedgers}
                    busy={busy}
                    locked={createLocked}
                    onMap={onMap}
                    onCreate={onCreate}
                  />
                </td>
              </tr>
            ) : (
              <tr key={`${row.kind}-${row.id}`} className="border-t align-top">
                <td className="px-3 py-2.5">
                  <RateName row={row} />
                  {row.expired ? (
                    <span className="mt-1 inline-flex rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Expired
                    </span>
                  ) : null}
                  {row.origin === "firm" ? (
                    <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">Added for this company</p>
                  ) : null}
                  {row.error ? <p className="mt-1 text-xs text-destructive">{row.error}</p> : null}
                </td>
                <td className="px-3 py-2.5">
                  <SideCell
                    row={row}
                    ledger={{
                      sample_name: row.sample_name,
                      status: row.status,
                      phase: row.phase,
                      tally_ledger_name: row.tally_ledger_name,
                      error: null,
                    }}
                    dutyLedgers={dutyLedgers}
                    busy={busy}
                    locked={createLocked}
                    onMap={onMap}
                    onCreate={onCreate}
                  />
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}

function SideCell({
  row,
  side,
  ledger,
  dutyLedgers,
  busy,
  locked,
  onMap,
  onCreate,
}: {
  row: TallyTaxMapRow;
  side?: "invoice" | "bill";
  ledger: TallyTaxSide;
  dutyLedgers: TallyDutyLedger[];
  busy: boolean;
  locked: boolean;
  onMap: (row: TallyTaxMapRow, ledgerName: string, side?: "invoice" | "bill") => void;
  onCreate: (row: TallyTaxMapRow, ledgerName: string, side?: "invoice" | "bill") => void;
}) {
  const pending = ledger.status === "pending";
  const mapped = ledger.status === "mapped";
  const names = dutyLedgers.map((item) => item.name);
  const selected = mapped ? ledger.tally_ledger_name ?? "" : "";
  const options = selected !== "" && !names.includes(selected) ? [selected, ...names] : names;

  if (pending) {
    return (
      <span className="text-muted-foreground">
        {ledger.phase === "confirming"
          ? `Reading the chart back to confirm ${ledger.tally_ledger_name}…`
          : `Creating ${ledger.tally_ledger_name} in Tally…`}
      </span>
    );
  }
  if (mapped) {
    return <span className="font-medium">{ledger.tally_ledger_name}</span>;
  }

  return (
    <div>
      <LedgerChoice
        sampleName={ledger.sample_name}
        options={options}
        selected={selected}
        busy={busy}
        locked={locked}
        onMap={(name) => onMap(row, name, side)}
        onCreate={(name) => onCreate(row, name, side)}
      />
      {ledger.error ? <p className="mt-1 text-xs text-destructive">{ledger.error}</p> : null}
    </div>
  );
}

function RateName({ row }: { row: TallyTaxMapRow }) {
  const catalogueName = (row.tax_name || row.applicability || "").trim();
  if ((row.kind === "tds" || row.kind === "tcs") && catalogueName !== "") {
    return (
      <div className="min-w-52">
        <div className="font-medium leading-5">{catalogueName}</div>
        <div className="text-xs text-muted-foreground">
          {row.section}
          {row.percentage !== "" ? ` · ${row.percentage}%` : ""}
        </div>
      </div>
    );
  }

  return <div className="font-medium">{row.sample_name}</div>;
}

function LedgerChoice({
  sampleName,
  options,
  selected,
  busy,
  locked,
  onMap,
  onCreate,
}: {
  sampleName: string;
  options: string[];
  selected: string;
  busy: boolean;
  locked: boolean;
  onMap: (ledgerName: string) => void;
  onCreate: (ledgerName: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(sampleName);
  const trimmed = name.trim();
  const waiting = busy || locked;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className={`${INPUT_CLASS} min-w-48`}
        value={selected}
        disabled={busy}
        aria-label={`Ledger for ${sampleName}`}
        onChange={(event) => {
          if (event.target.value !== "") onMap(event.target.value);
        }}
      >
        <option value="">Choose a ledger</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {renaming ? (
        <>
          <input
            className={`${INPUT_CLASS} min-w-48`}
            value={name}
            maxLength={200}
            disabled={waiting}
            aria-label={`Ledger name, suggested ${sampleName}`}
            onChange={(event) => setName(event.target.value)}
          />
          <CoButton
            type="button"
            variant="outline"
            disabled={waiting || trimmed === ""}
            title={locked ? "Wait until the ledger already being created in Tally is finished." : undefined}
            onClick={() => onCreate(trimmed)}
          >
            Create in Tally
          </CoButton>
        </>
      ) : (
        <CoButton
          type="button"
          variant="outline"
          disabled={waiting}
          title={locked ? "Wait until the ledger already being created in Tally is finished." : `Create ${sampleName} in Tally`}
          onClick={() => onCreate(sampleName)}
        >
          Create {sampleName}
        </CoButton>
      )}
      <button
        type="button"
        className="text-xs text-muted-foreground underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
        disabled={waiting}
        onClick={() => setRenaming((open) => !open)}
      >
        {renaming ? "Use suggested name" : "Rename"}
      </button>
    </div>
  );
}
